import { stampGroupPost } from "@/src/services/api/groups/groups-function";
import { countersEnabled, recountPost } from "@/src/services/api/posts/engagement-functions";
import { feedRankingEnabled, rankedPage, isFeedCursorExpired } from "./feed-functions";
import { useAuth } from "@/src/contexts/auth.context";
import { deleteEntityFiles } from "@/src/services/api/storage/storage.service";

export { deduplicateReactionsByUser } from "@/src/utils/reaction-dedup.utils";

/**
 * Per-postId serialization lock: ensures concurrent reaction taps on the same
 * post execute sequentially so each sees the committed state of the previous.
 */
const reactionMutationLocks = new Map<string, Promise<void>>();
import {
  CreateCommentPayload,
  CreatePostPayload,
  Post,
  ReactionType,
  ReactToCommentPayload,
  ReactToPostPayload,
  UpdatePostPayload,
  UpdatePostScorePayload,
} from "@/src/models/post.model";
import {
  COMMENTS_QUERY_KEY,
  POSTS_QUERY_KEY,
  REACTIONS_QUERY_KEY,
} from "@/src/query-keys/post.keys";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isOfflineError } from "@/src/services/cache/offline";
import { OPTIMISTIC_FLAG } from "@/src/services/cache/query-persistence";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { useGetUserProfileQuery } from "../../profile/profile.hooks";
import { primeLinkPreviewFromContent } from "../link-preview/link-preview.service";
import gqlClient from "../graphql.client";
import {
  CREATE_COMMENT_MUTATION,
  CREATE_POST_MUTATION,
  DELETE_COMMENT_MUTATION,
  DELETE_POST_MUTATION,
  DELETE_REACTION_BY_COMMENT_MUTATION,
  DELETE_REACTION_BY_POST_MUTATION,
  GET_COMMENTS_QUERY,
  GET_POSTS_QUERY,
  GET_POST_CARDS_QUERY,
  GET_REACTIONS,
  GET_USER_REACTION_QUERY,
  GET_USER_REACTIONS_FOR_POSTS,
  INSERT_REACTION_MUTATION,
  UPDATE_COMMENT_REACTION_COUNT_MUTATION,
  UPDATE_COMMENT_REPLY_COUNT_MUTATION,
  UPDATE_POST_COMMENT_COUNT_MUTATION,
  UPDATE_POST_MUTATION,
  UPDATE_POST_REACTION_COUNT_MUTATION,
  UPDATE_POST_SCORE_MUTATION,
  UPDATE_REACTION_MUTATION,
} from "./posts.query";
import apiClient from "../axios";
import { FeedSort, FEED_SORT_ORDER } from "@/src/utils/feed-sort";
import { groupScopeWhere } from "@/src/utils/group-post-scope";

export type { FeedSort };
export { FEED_SORT_ORDER };

/**
 * Feed page size.
 *
 * Small on purpose: a page is about one screenful, and the next one is only
 * requested once the reader has actually reached the end of this one (see
 * `loadMore` in `use-posts`). Fetching ten at a time meant the first screen
 * paid for rows nobody scrolled to.
 */
export const FEED_PAGE_SIZE = 5;

/**
 * How long a resolved "did I react to this post" answer is trusted.
 *
 * It only changes when this user reacts, and that path writes the cache
 * directly, so re-asking on every remount is pure noise. This is also what
 * makes the batched seed below effective: the per-post queries wake up, find
 * fresh data and never hit the network.
 */
const USER_REACTION_STALE_TIME = 5 * 60 * 1000;

type FeedPage = {getposts: {items: Post[]; hasNextPage: boolean; nextCursor: string | null; pageNo: number; totalCount?: number}};

export const fetchFeedPage = async (groupId: string | undefined, sort: FeedSort, pageParam: number | string = 1, userId?: string, orgId?: string): Promise<FeedPage> => {
  // Group feeds read directly too. They used to go through the ranking
  // Function, which made the tab hostage to its run queue: a run stuck in
  // QUEUED meant no `getposts` request was ever sent, the tab stayed empty, and
  // the result poll (plus query retries) kept hammering `fn/runs/…/result`.
  const pageNo = typeof pageParam === "number" && Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const client = await gqlClient();
  // `GET_POSTS_QUERY` takes `$where: postFilterInput` — and `getposts` has no
  // `filter` argument at all. This used to send `filter: JSON.stringify(...)`,
  // an undeclared variable the gateway silently drops, so a group feed was
  // really fetching *every* post and leaning on the client-side filter in
  // use-posts.ts to hide the rest. That made a group's own posts hostage to
  // pagination: with pageSize 10 over the whole tenant, a group's post is
  // only visible once its page happens to be fetched.
  //
  // A group post carries its group in `groupId`, but a post authored outside
  // the group and targeted at it carries the id in `visibleToGroupIds`
  // instead, so both have to be matched — this mirrors what the in-group
  // branch of `filterPostVisibility` accepts.
  const result = await client.request<{getposts: {items: Post[]}}>(GET_POST_CARDS_QUERY, {
    where: groupId ? groupScopeWhere(groupId) : { and: [{ OrganizationId: { eq: orgId ?? "__no_org__" } }, { or: [
      { visibility: { eq: "public" } },
      ...(userId ? [
        { and: [{ visibility: { eq: "private" } }, { CreatedBy: { eq: userId } }] },
        { and: [{ visibility: { eq: "specific" } }, { visibleToUserIds: { in: [userId] } }] },
      ] : []),
    ] }] },
    order: FEED_SORT_ORDER[sort],
    pageNo,
    pageSize: FEED_PAGE_SIZE,
  });
  return {getposts: {...result.getposts, items: result.getposts.items.slice(0, FEED_PAGE_SIZE),
    hasNextPage: result.getposts.items.length === FEED_PAGE_SIZE, nextCursor: null, pageNo}};
};

export const feedQueryKey = (groupId: string | undefined, sort: FeedSort, orgId?: string, userId?: string) =>
  [...(groupId ? [POSTS_QUERY_KEY, groupId, sort] : [POSTS_QUERY_KEY, sort]), "fast-first", orgId ?? "", userId ?? ""];

/** Put authorized background cards in the normal mutation-aware list cache.
 * Page numbers and direct continuation stay unchanged; existing card state wins.
 */
export const mergeBackgroundCards = (old: any, cards: Post[]) => {
  if (!old?.pages?.length || !cards.length) return old;
  const known = new Set<string>(old.pages.flatMap((page: any) =>
    (page?.getposts?.items ?? []).map((post: Post) => post.ItemId)));
  const added = cards.filter(post => {if (known.has(post.ItemId)) return false; known.add(post.ItemId); return true;});
  if (!added.length) return old;
  return {...old, pages: old.pages.map((page: any, index: number) => index ? page : {
    ...page, getposts: {...page.getposts, items: [...page.getposts.items, ...added]},
  })};
};

export const useGetPostsQuery = (groupId?: string, sort: FeedSort = "hottest") => {
  const {user, orgId} = useAuth() ?? {};
  return useInfiniteQuery({
    queryKey: feedQueryKey(groupId, sort, orgId, user?.itemId),
    queryFn: ({ pageParam = 1 }) => fetchFeedPage(groupId, sort, pageParam, user?.itemId, orgId),
    initialPageParam: 1 as number | string,
    getNextPageParam: (lastPage) => {
      const { hasNextPage, pageNo } = lastPage?.getposts ?? {};
      return hasNextPage ? pageNo + 1 : undefined;
    },
    retry: (count, error) => !isFeedCursorExpired(error) && count < 2,
    staleTime: 1000 * 60 * 2,
  });
};

/** How often the feed checks for new or newly boosted posts while open. */
export const FEED_HEAD_INTERVAL_MS = 60 * 1000;
export const FEED_HEAD_QUERY_KEY = "feedHead";
/** A head entry; `post` is there when the check had the record to hand. */
export type FeedHeadItem = { id: string; post?: Post; nextCursor?: string | null };

/**
 * The ids on the first page of the feed, checked every minute while the app is
 * in front. The full list is not refetched for this (that would re-run every
 * loaded page); the feed compares these ids with what it shows to offer the
 * "N new posts" pill, or to quietly re-sort when the reader is at the top.
 * Not persisted: it is only ever a fresh signal.
 */
export const useFeedHeadQuery = (
  sort: FeedSort,
  groupId?: string,
  enabled = true,
) => {
  const {user, orgId} = useAuth() ?? {};
  return useQuery({
    queryKey: [FEED_HEAD_QUERY_KEY, orgId, user?.itemId, groupId ?? "", sort],
    queryFn: async (): Promise<FeedHeadItem[]> => {
      if (feedRankingEnabled) {
        const page = await rankedPage(sort, groupId, undefined, 20);
        return page.getposts.items.map((post) => ({id: post.ItemId, post, nextCursor: page.getposts.nextCursor}));
      }
      const result = await fetchFeedPage(groupId, sort, 1, user?.itemId, orgId);
      return (result?.getposts?.items ?? []).map((post: Post) => ({
        id: post.ItemId,
        post,
      }));
    },
    enabled,
    staleTime: FEED_HEAD_INTERVAL_MS,
    refetchInterval: FEED_HEAD_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: 0,
    meta: { persist: false },
  });
};

export const useGetPostByIdQuery = (
  postId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [POSTS_QUERY_KEY, postId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        where: { ItemId: { eq: postId } },
        order: [],
        pageNo: 1,
        pageSize: 1,
      });
      return (result?.getposts?.items?.[0] as Post) ?? null;
    },
    enabled: options?.enabled ?? true,
  });
};

export const useCreatePostMutation = (groupId?: String) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreatePostPayload) => {
      const client = await gqlClient();
      const created: any = await client.request(CREATE_POST_MUTATION, {
        content: payload.content,
        hashtags: payload.hashtags,
        mentionedUsers: payload.mentionedUsers?.map((user) => {
          const [firstName = "", ...rest] = (user.displayName ?? "").split(" ");
          return {
            firstName,
            lastName: rest.join(" "),
            userId: user.itemId,
            profileImageUrl: user.profileImageUrl,
            email: user.email,
            displayName: user.displayName,
          };
        }),
        visibility: payload.visibility ?? "public",
        attachmentUrls: payload.attachmentUrls ?? [],
        score: payload.score ?? 0,
        groupId: payload.groupId ?? null,
        visibleToGroupIds: payload.visibleToGroupIds ?? [],
        visibleToUserIds: payload.visibleToUserIds ?? [],
        lifeEventType: payload.lifeEventType ?? null,
        lifeEventDate: payload.lifeEventDate ?? null,
        // Only ever set by the news mirror (news-post.service); the composer
        // leaves them null, which is what marks a post as an ordinary post.
        newsId: payload.newsId ?? null,
        newsTitle: payload.newsTitle ?? null,
        ...payload.authorSnapshot,
      });
      // Group audience (memberIds) is server-maintained; ask for the stamp.
      await stampGroupPost(created?.insertpost?.itemId, payload.visibility);
      return created;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [FEED_HEAD_QUERY_KEY] });
      Toast.show({ type: "success", text1: t("SUCCESS_POST_CREATED") });
      if (variables.lifeEventType) {
        Toast.show({ type: "info", text1: t("LABEL_LIFE_UPDATE_UNSAVED") });
      }
      // Extract and cache the link preview now that the post exists, so the
      // feed usually finds the row already warm. Deliberately not awaited and
      // deliberately after the insert: a URL that cannot be read must never
      // stop a post from being created.
      void primeLinkPreviewFromContent(variables.content);
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_CREATE_POST") });
      console.error(error);
    },
  });
};

export const useUpdatePostMutation = (groupId?: String) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {user} = useAuth();

  return useMutation({
    mutationFn: async (payload: UpdatePostPayload) => {
      const client = await gqlClient();
      return client.request(UPDATE_POST_MUTATION, {
        itemId: payload.id,
        content: payload.content,
        hashtags: payload.hashtags,
        mentionedUsers: payload.mentionedUsers?.map((user) => {
          const [firstName = "", ...rest] = (user.displayName ?? "").split(" ");
          return {
            firstName,
            lastName: rest.join(" "),
            userId: user.itemId,
            profileImageUrl: user.profileImageUrl,
            email: user.email,
            displayName: user.displayName,
          };
        }),
        visibility: payload.visibility,
        attachmentUrls: payload.attachmentUrls ?? [],
        visibleToGroupIds: payload.visibleToGroupIds ?? [],
        visibleToUserIds: payload.visibleToUserIds ?? [],
        lifeEventType: payload.lifeEventType,
        lifeEventDate: payload.lifeEventDate,
        // Left undefined by the composer, so the variable is omitted and the
        // stored headline of a news mirror survives an unrelated update.
        newsTitle: payload.newsTitle,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["USER_POSTS", user?.itemId]});
      Toast.show({ type: "success", text1: t("SUCCESS_POST_UPDATED") });
      queryClient.invalidateQueries({ queryKey: [FEED_HEAD_QUERY_KEY] });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_UPDATE_POST") });
      console.error(error);
    },
  });
};


export const useUpdatePostScoreMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: UpdatePostScorePayload) => {
      // With server counters every reaction/comment/save recount already rewrote the score.
      if (countersEnabled) return null;
      const client = await gqlClient();
      return client.request(UPDATE_POST_SCORE_MUTATION, {
        itemId: payload.id,
        score: payload.score ?? 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_QUERY_KEY] });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_UPDATE_POST") });
      console.error(error);
    },
  });
};

export const useDeletePostMutation = (groupId?: String) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {user} = useAuth();

  return useMutation({
    /**
     * Accepts either a bare id (existing call sites) or `{itemId, entity}`. When
     * the entity is supplied, its uploaded attachments are deleted from Blocks
     * Storage too — otherwise they are orphaned there forever.
     */
    mutationFn: async (input: string | { itemId: string; entity?: unknown }) => {
      const { itemId, entity } =
        typeof input === "string" ? { itemId: input, entity: undefined } : input;
      const client = await gqlClient();
      const result = await client.request(DELETE_POST_MUTATION, { itemId });
      await deleteEntityFiles(entity);
      return result;
    },
    onSuccess: (_result, input) => {
      const itemId = typeof input === "string" ? input : input.itemId;
      queryClient.setQueriesData?.({queryKey: [FEED_HEAD_QUERY_KEY]}, (old: FeedHeadItem[] | undefined) =>
        Array.isArray(old) ? old.filter(item => item.id !== itemId) : old);
      queryClient.invalidateQueries({ queryKey: [FEED_HEAD_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["USER_POSTS", user?.itemId]});
      Toast.show({ type: "success", text1: t("SUCCESS_POST_DELETED") });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_DELETE_POST") });
      console.error(error);
    },
  });
};

export type UserPostReaction = {
  type: ReactionType;
  id: string | null;
};

/** Cache slot for "has this user reacted to this post". */
export const userPostReactionKey = (postId: string, userId: string) => [
  REACTIONS_QUERY_KEY,
  postId,
  userId,
];

/**
 * This user's reaction to one post.
 *
 * `options.enabled` exists so a list can switch it off while a batched lookup
 * fills the cache for the whole page at once — see
 * `useSeedUserPostReactions`. On its own (the post detail modal), it behaves
 * exactly as it always has.
 */
export const useGetUserPostReaction = (
  postId: string,
  userId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: userPostReactionKey(postId, userId),
    queryFn: async () => {
      const client = await gqlClient();
      const res = await client.request(GET_USER_REACTION_QUERY, {
        where: { postId: { eq: postId }, userId: { eq: userId } },
      });
      // Comment reactions carry postId too; only a row without commentId is the post reaction.
      const item = (res.getreactions?.items ?? []).find((r: any) => !r?.commentId);

      return {
        type: (item?.type as ReactionType) ?? null,
        id: item?.ItemId ?? null,
      } satisfies UserPostReaction;
    },
    enabled: !!postId && !!userId && (options?.enabled ?? true),
    staleTime: USER_REACTION_STALE_TIME,
  });
};

/**
 * Resolve this user's reactions for a whole page of posts in one request, and
 * write each answer into the slot the per-post query reads.
 *
 * Returns whether the cache is ready, so a list can hold its cards' individual
 * queries until the batch has landed. `ready` is also true when the batch
 * FAILS: the per-post queries are the fallback, and blocking them on a broken
 * batch would leave the feed with no reaction state at all.
 */
export const useSeedUserPostReactions = (
  postIds: readonly string[],
  userId: string,
) => {
  const queryClient = useQueryClient();

  // Only ids with nothing cached yet. As later pages arrive this shrinks to
  // just the new ones, so page two costs one request for five posts, not six.
  const missing = postIds.filter(
    (postId) =>
      queryClient.getQueryData(userPostReactionKey(postId, userId)) ===
      undefined,
  );
  const missingKey = missing.join(",");

  const { isFetched, isError } = useQuery({
    queryKey: [REACTIONS_QUERY_KEY, "user-batch", userId, missingKey],
    queryFn: async () => {
      const ids = missingKey.split(",").filter(Boolean);
      const client = await gqlClient();
      const res = await client.request(GET_USER_REACTIONS_FOR_POSTS, {
        where: { postId: { in: ids }, userId: { eq: userId } },
        // Comment reactions share postId; leave room for them so no post's own row is cut off.
        pageSize: Math.max(50, ids.length * 10),
      });

      const byPostId = new Map<string, UserPostReaction>();
      for (const item of res.getreactions?.items ?? []) {
        if (!item?.postId || item.commentId || byPostId.has(item.postId)) continue;
        byPostId.set(item.postId, {
          type: (item.type as ReactionType) ?? null,
          id: item.ItemId ?? null,
        });
      }

      // Posts the user has NOT reacted to are seeded too — "no reaction" is an
      // answer, and leaving those slots empty would send every unreacted post
      // off to fetch the nothing we already know about.
      ids.forEach((postId) => {
        queryClient.setQueryData(
          userPostReactionKey(postId, userId),
          byPostId.get(postId) ?? { type: null, id: null },
        );
      });

      return ids.length;
    },
    enabled: !!userId && missing.length > 0,
    staleTime: USER_REACTION_STALE_TIME,
    // A failed batch is not worth retrying — the per-post queries take over.
    retry: 0,
  });

  return { ready: missing.length === 0 || isFetched || isError };
};

/**
 * Page size for the reactor list — one scroll pulls one page of this many rows.
 * The list is filtered by reaction type on the client, so a page can contribute
 * only a few rows to the tab the viewer is looking at; the modal tops up a
 * sparse tab one page at a time rather than asking for a bigger page.
 */
const REACTIONS_PAGE_SIZE = 10;

export const useGetReactions = (postId: string) => {
  return useInfiniteQuery({
    queryKey: [REACTIONS_QUERY_KEY, postId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const result = await client.request(GET_REACTIONS, {
        where: { postId: { eq: postId } },
        order: [{ field: "CreatedDate", direction: "DESC" }],
        pageNo: pageParam,
        pageSize: REACTIONS_PAGE_SIZE,
      });
      return result;
    },
    // Page numbers come from how many pages we already hold, not from the
    // response: GET_REACTIONS does not select `pageNo`, so reading it back gave
    // `undefined + 1` -> NaN. The gateway then received `pageNo: null`, answered
    // with page one again, and still said `hasNextPage: true` — so react-query
    // believed a next page existed forever and a single scroll at the end of the
    // list re-fetched the same rows without end.
    //
    // `totalPages` is the authoritative stop, and unlike `hasNextPage` it is
    // consistent with the page we actually asked for.
    getNextPageParam: (lastPage, allPages) => {
      const page = lastPage?.getreactions;
      if (!page) return undefined;

      const totalPages = page.totalPages ?? 0;
      if (totalPages > 0) {
        return allPages.length < totalPages ? allPages.length + 1 : undefined;
      }

      // No `totalPages` to go on: trust `hasNextPage`, but treat a page the
      // server could not fill as the last one.
      if (!page.hasNextPage) return undefined;
      if ((page.items?.length ?? 0) < REACTIONS_PAGE_SIZE) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
    // The reactor list is mounted alongside a post, which can be nothing yet.
    enabled: !!postId,
  });
};

export const useReactToPostMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      postId,
      userId,
      newReaction,
      previousReaction,
      currentCounts,
      reactorName,
      reactorProfileImageUrl,
    }: ReactToPostPayload) => {
      // Serialize: wait for any in-flight mutation on the same post
      const prevLock = reactionMutationLocks.get(postId) ?? Promise.resolve();
      let unlock!: () => void;
      reactionMutationLocks.set(
        postId,
        new Promise<void>((resolve) => { unlock = resolve; }),
      );

      try {
        await prevLock;
        const client = await gqlClient();

        // Server-side resolution: fetch the actual current row so we never
        // insert a duplicate after an onError null-out or a concurrent tap.
        const serverRes = await client.request(GET_USER_REACTION_QUERY, {
          where: { postId: { eq: postId }, userId: { eq: userId } },
        });
        const serverItem = serverRes.getreactions?.items?.[0];
        const resolvedId: string | null = serverItem?.ItemId ?? null;

        let reactionId: string | null = resolvedId;

        // 1. Update the Reaction collection
        if (!newReaction) {
          await client.request(DELETE_REACTION_BY_POST_MUTATION, { postId, userId });
          reactionId = null;
        } else if (resolvedId) {
          await client.request(UPDATE_REACTION_MUTATION, {
            itemId: resolvedId,
            type: newReaction,
          });
        } else {
          const res = await client.request(INSERT_REACTION_MUTATION, {
            userId,
            postId,
            type: newReaction,
            reactorName,
            reactorProfileImageUrl,
          });
          reactionId = res.insertreaction.itemId;
        }

        // 2. Build updated counts
        const updatedCounts = { ...currentCounts };
        if (previousReaction) {
          updatedCounts[previousReaction] = Math.max(
            0,
            (updatedCounts[previousReaction] ?? 0) - 1,
          );
        }
        if (newReaction) {
          updatedCounts[newReaction] = (updatedCounts[newReaction] ?? 0) + 1;
        }

        // 3. Sync post reaction counts — non-fatal; the reaction row is committed
        try {
          if (countersEnabled) {
            // Server-side recount (club-post-counters-v1): post edits are owner/admin only.
            const server = await recountPost(postId);
            if (server?.reactionCount) Object.assign(updatedCounts, server.reactionCount);
          } else {
            await client.request(UPDATE_POST_REACTION_COUNT_MUTATION, {
              itemId: postId,
              reactionCount: updatedCounts,
            });
          }
        } catch {
          console.warn('useReactToPostMutation: count sync failed; reaction row committed, count will self-heal on next invalidation');
        }

        return { updatedCounts, reactionId };
      } finally {
        unlock();
      }
    },

    onMutate: async ({
      postId,
      userId,
      newReaction,
      previousReaction,
      currentCounts,
    }) => {
      await queryClient.cancelQueries({ queryKey: [POSTS_QUERY_KEY] });
      await queryClient.cancelQueries({
        queryKey: [REACTIONS_QUERY_KEY, postId, userId],
      });

      const patchCounts = (counts: any) => {
        const updated = { ...counts };
        if (previousReaction)
          updated[previousReaction] = Math.max(
            0,
            (updated[previousReaction] ?? 0) - 1,
          );
        if (newReaction) updated[newReaction] = (updated[newReaction] ?? 0) + 1;
        return updated;
      };

      // Snapshot every posts-list query the cache currently holds — one entry
      // per active `sort`/`groupId` combination — so onError can restore them.
      // A single exact key (the previous `[POSTS_QUERY_KEY, ""]`) missed every
      // real feed key (`[POSTS_QUERY_KEY, sort]` / `[POSTS_QUERY_KEY, groupId,
      // sort]` from useGetPostsQuery), so the optimistic write below landed in
      // an orphan cache entry nothing was subscribed to — the feed only ever
      // updated on a manual refresh. `setQueriesData` with a partial key
      // patches every matching cache entry generically instead of enumerating
      // every sort/group variant by hand.
      const previousPostsQueries = queryClient.getQueriesData({
        queryKey: [POSTS_QUERY_KEY],
      });
      const previousReactionCache = queryClient.getQueryData([
        REACTIONS_QUERY_KEY,
        postId,
        userId,
      ]);

      queryClient.setQueriesData({ queryKey: [POSTS_QUERY_KEY] }, (old: any) => {
        // Only the infinite-list shape has `.pages` — this also matches the
        // single-post query below (`[POSTS_QUERY_KEY, postId]`), which doesn't,
        // so it's a harmless no-op there and handled explicitly next.
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => {
            if (!page?.getposts?.items) return page;
            return {
              ...page,
              getposts: {
                ...page.getposts,
                items: page.getposts.items.map((post: any) =>
                  post.ItemId === postId
                    ? { ...post, reactionCount: patchCounts(currentCounts) }
                    : post,
                ),
              },
            };
          }),
        };
      });

      const previousPost = queryClient.getQueryData([POSTS_QUERY_KEY, postId]);
      // Optimistically update the single post cache too
      queryClient.setQueryData([POSTS_QUERY_KEY, postId], (old: any) => {
        if (!old) return old;
        return { ...old, reactionCount: patchCounts(currentCounts) };
      });

      // Optimistically update user reaction in cache
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, postId, userId],
        (old: any) => ({
          id: old?.id ?? null,
          type: newReaction,
        }),
      );

      return { previousPostsQueries, previousPost, previousReactionCache };
    },

    onSuccess: (data, { postId, userId, newReaction }) => {
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, postId, userId],
        (_old: any) => ({
          id: newReaction ? data.reactionId : null,
          type: newReaction ?? null,
        }),
      );
      // Reconcile with the server's real computed count in case it drifted
      // from the optimistic +1/-1 (e.g. rapid taps), and refresh the
      // "who reacted" list — neither was touched by the optimistic update.
      //
      // The feed lists are only marked stale, not refetched: refetching every
      // loaded page (and a fresh ranking snapshot) after each tap was the most
      // expensive thing the feed did, and the optimistic count is already on
      // screen. The open post itself is refetched for the exact count.
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY, postId],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY],
        refetchType: "none",
      });
      queryClient.invalidateQueries({ queryKey: [REACTIONS_QUERY_KEY, postId] });
    },

    onError: (_err, { postId, userId }, context) => {
      // No context: the mutation was refused before onMutate ran (offline), so
      // nothing optimistic was written and there is nothing to roll back.
      // Restoring from an undefined context would null the reaction cache,
      // which is exactly what CR-B3-04 warns about.
      if (!context) return;
      context?.previousPostsQueries?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      queryClient.setQueryData([POSTS_QUERY_KEY, postId], context?.previousPost);
      // Restore to the pre-mutation state — never null, which would cause a
      // duplicate INSERT on the very next reaction attempt (CR-B3-04).
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, postId, userId],
        context?.previousReactionCache ?? null,
      );
    },

    onSettled: (_data, err, { postId, userId }) => {
      if (err && !isOfflineError(err)) {
        queryClient.invalidateQueries({ queryKey: [POSTS_QUERY_KEY] });
        queryClient.invalidateQueries({
          queryKey: [REACTIONS_QUERY_KEY, postId, userId],
        });
      }
    },
  });
};

// comments
export const useGetCommentsQuery = (
  postId: string,
  parentCommentId?: string,
) => {
  return useInfiniteQuery({
    queryKey: [COMMENTS_QUERY_KEY, postId, parentCommentId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      return client.request(GET_COMMENTS_QUERY, {
        where: { postId: { eq: postId } },
        parentCommentId: parentCommentId ?? null,
        pageNo: pageParam,
        pageSize: 10,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage?.getcomments?.hasNextPage
        ? lastPage.getcomments.pageNo + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!postId,
  });
};

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateCommentPayload) => {
      const client = await gqlClient();

      // Create the comment
      const result = await client.request(CREATE_COMMENT_MUTATION, {
        postId: payload.postId,
        authorId: payload.authorId,
        parentCommentId: payload.parentCommentId ?? null,
        content: payload.content,
        ...payload.authorSnapshot,
        replyCount: payload.replyCount,
        mentionedUsers: payload.mentionedUsers?.map((u) => ({
          userId: u.itemId,
          firstName: (u.displayName ?? '').split(' ')[0] ?? '',
          lastName: (u.displayName ?? '').split(' ').slice(1).join(' '),
          profileImageUrl: u.profileImageUrl,
          displayName: u.displayName,
        })) ?? null,
      });
      if (countersEnabled) {
        await recountPost(payload.postId);
      } else {
        await client.request(UPDATE_POST_COMMENT_COUNT_MUTATION, {
          itemId: payload.postId,
          commentCount: (payload.postCommentCount ?? 0) + 1,
        });
      }
      // If it's a reply, increment parent comment replyCount in DB
      if (payload.parentCommentId && payload.parentReplyCount !== undefined) {
        await client.request(UPDATE_COMMENT_REPLY_COUNT_MUTATION, {
          itemId: payload.parentCommentId,
          replyCount: payload.parentReplyCount + 1,
        });
      }

      return result;
    },

    onMutate: async (payload: CreateCommentPayload) => {
      await queryClient.cancelQueries({
        queryKey: [COMMENTS_QUERY_KEY, payload.postId, undefined],
      });

      const previousComments = queryClient.getQueryData([
        COMMENTS_QUERY_KEY,
        payload.postId,
        undefined,
      ]);

      // Optimistically increment replyCount on parent comment
      if (payload.parentCommentId) {
        queryClient.setQueryData(
          [COMMENTS_QUERY_KEY, payload.postId, undefined],
          (old: any) => {
            if (!old?.pages) return old;
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                getcomments: {
                  ...page.getcomments,
                  items: page.getcomments.items.map((comment: any) => {
                    if (comment.ItemId !== payload.parentCommentId)
                      return comment;
                    return {
                      ...comment,
                      replyCount: (comment.replyCount ?? 0) + 1,
                    };
                  }),
                },
              })),
            };
          },
        );
      }
      // Show the comment straight away. It is flagged so it is never
      // persisted, and the refetch after success replaces it with the real one.
      const optimisticComment = {
        ItemId: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        postId: payload.postId,
        parentCommentId: payload.parentCommentId ?? null,
        content: payload.content,
        replyCount: 0,
        authorSnapshot: {
          firstName: payload.authorSnapshot.firstName ?? "",
          lastName: payload.authorSnapshot.lastName ?? "",
          email: payload.authorSnapshot.email ?? "",
          profileImageUrl: payload.authorSnapshot.profileImageUrl ?? "",
        },
        reactionCount: {},
        mentionedUsers: (payload.mentionedUsers ?? []).map((u) => ({
          userId: u.itemId,
          firstName: (u.displayName ?? "").split(" ")[0] ?? "",
          lastName: (u.displayName ?? "").split(" ").slice(1).join(" "),
          profileImageUrl: u.profileImageUrl,
        })),
        CreatedDate: new Date().toISOString(),
        CreatedBy: payload.authorId,
        [OPTIMISTIC_FLAG]: true,
      };
      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, payload.postId, undefined],
        (old: any) => {
          if (!old?.pages?.length) return old;
          const last = old.pages.length - 1;
          return {
            ...old,
            pages: old.pages.map((page: any, index: number) =>
              index === last
                ? {
                    ...page,
                    getcomments: {
                      ...page.getcomments,
                      items: [...(page.getcomments?.items ?? []), optimisticComment],
                    },
                  }
                : page,
            ),
          };
        },
      );

      const previousPost = queryClient.getQueryData([
        POSTS_QUERY_KEY,
        payload.postId,
      ]);
      // Every feed list holding the post (any sort, any group), not the
      // `[POSTS_QUERY_KEY, ""]` key this used to target, which no list uses.
      const previousPostsQueries = queryClient.getQueriesData({
        queryKey: [POSTS_QUERY_KEY],
      });

      // Optimistically increment commentCount on single post cache
      queryClient.setQueryData(
        [POSTS_QUERY_KEY, payload.postId],
        (old: any) => {
          if (!old) return old;
          return { ...old, commentCount: (old.commentCount ?? 0) + 1 };
        },
      );

      // Optimistically increment commentCount on post list cache
      queryClient.setQueriesData({ queryKey: [POSTS_QUERY_KEY] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) =>
            page?.getposts?.items
              ? {
                  ...page,
                  getposts: {
                    ...page.getposts,
                    items: page.getposts.items.map((post: any) =>
                      post.ItemId !== payload.postId
                        ? post
                        : { ...post, commentCount: (post.commentCount ?? 0) + 1 },
                    ),
                  },
                }
              : page,
          ),
        };
      });

      return { previousComments, previousPost, previousPostsQueries };
    },

    onError: (error, payload, context) => {
      // No context: refused before onMutate (offline) — nothing to undo, and
      // the offline guard has already told the user.
      if (!context) return;
      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, payload.postId, undefined],
        context.previousComments,
      );
      queryClient.setQueryData(
        [POSTS_QUERY_KEY, payload.postId],
        context.previousPost,
      );
      context.previousPostsQueries?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      Toast.show({ type: "error", text1: t("ERROR_CREATE_COMMENT") });
      console.error(error);
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_QUERY_KEY, variables.postId],
      });
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY, variables.postId],
        exact: true,
      });
      // Lists keep the optimistic count; refreshed on their next fetch.
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY],
        refetchType: "none",
      });

      Toast.show({ type: "success", text1: t("SUCCESS_SEND_COMMENT") });
    },
  });
};

// comment reactions
export const useGetUserCommentReaction = (
  commentId: string,
  userId: string,
) => {
  return useQuery({
    queryKey: [REACTIONS_QUERY_KEY, commentId, userId],
    queryFn: async () => {
      const client = await gqlClient();
      const res = await client.request(GET_USER_REACTION_QUERY, {
        where: { commentId: { eq: commentId }, userId: { eq: userId } },
      });
      const item = res.getreactions?.items?.[0];
      return {
        type: (item?.type as ReactionType) ?? null,
        id: item?.ItemId ?? null,
      };
    },
    enabled: !!commentId && !!userId,
  });
};

export const useReactToCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      postId,
      userId,
      newReaction,
      previousReaction,
      currentCounts,
      existingReactionId,
    }: ReactToCommentPayload) => {
      const client = await gqlClient();

      let reactionId = existingReactionId;

      if (!newReaction) {
        await client.request(DELETE_REACTION_BY_COMMENT_MUTATION, {
          commentId,
          userId,
        });
      } else if (existingReactionId) {
        await client.request(UPDATE_REACTION_MUTATION, {
          itemId: existingReactionId,
          type: newReaction,
        });
      } else {
        const res = await client.request(INSERT_REACTION_MUTATION, {
          userId,
          postId,
          commentId,
          type: newReaction,
        });
        reactionId = res.insertreaction.itemId;
      }

      const updatedCounts = { ...currentCounts };
      if (previousReaction) {
        updatedCounts[previousReaction] = Math.max(
          0,
          (updatedCounts[previousReaction] ?? 0) - 1,
        );
      }
      if (newReaction) {
        updatedCounts[newReaction] = (updatedCounts[newReaction] ?? 0) + 1;
      }

      // Sync comment reaction counts
      await client.request(UPDATE_COMMENT_REACTION_COUNT_MUTATION, {
        itemId: commentId,
        reactionCount: updatedCounts,
      });

      return { updatedCounts, reactionId };
    },

    onMutate: async ({
      commentId,
      postId,
      userId,
      newReaction,
      previousReaction,
    }) => {
      await queryClient.cancelQueries({
        queryKey: [COMMENTS_QUERY_KEY, postId, undefined],
      });
      await queryClient.cancelQueries({
        queryKey: [REACTIONS_QUERY_KEY, commentId, userId],
      });

      const previousComments = queryClient.getQueryData([
        COMMENTS_QUERY_KEY,
        postId,
        undefined,
      ]);
      // Snapshot so onError can restore instead of nulling — nulling would
      // cause a duplicate INSERT on the next reaction attempt, same failure
      // mode as CR-B3-04 on the post-reaction mutation above.
      const previousReactionCache = queryClient.getQueryData([
        REACTIONS_QUERY_KEY,
        commentId,
        userId,
      ]);

      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, postId, undefined],
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              getcomments: {
                ...page.getcomments,
                items: page.getcomments.items.map((comment: any) => {
                  if (comment.ItemId !== commentId) return comment;

                  // read live counts from cache, not from prop
                  const liveCounts = { ...comment.reactionCount };
                  if (previousReaction)
                    liveCounts[previousReaction] = Math.max(
                      0,
                      (liveCounts[previousReaction] ?? 0) - 1,
                    );
                  if (newReaction)
                    liveCounts[newReaction] =
                      (liveCounts[newReaction] ?? 0) + 1;
                  return { ...comment, reactionCount: liveCounts };
                }),
              },
            })),
          };
        },
      );

      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, commentId, userId],
        (old: any) => ({
          id: old?.id ?? null,
          type: newReaction,
        }),
      );

      return { previousComments, previousReactionCache };
    },

    onSuccess: (data, { commentId, postId, userId, newReaction }) => {
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, commentId, userId],
        (old: any) => ({
          id: newReaction ? data.reactionId : null,
          type: newReaction ?? null,
        }),
      );
      // Reconcile the comment list's counts with the server, and refresh the
      // "who reacted" list — neither is guaranteed by the optimistic patch
      // above once rapid taps or a stale cache are in play.
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_QUERY_KEY, postId],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [REACTIONS_QUERY_KEY, commentId],
      });
    },

    onError: (_err, { postId, commentId, userId }, context) => {
      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, postId, undefined],
        context?.previousComments,
      );
      // Restore to the pre-mutation state — never null, which would cause a
      // duplicate INSERT on the very next reaction attempt (CR-B3-04).
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, commentId, userId],
        context?.previousReactionCache ?? null,
      );
    },

    onSettled: (_data, err, { postId, commentId, userId }) => {
      if (err) {
        queryClient.invalidateQueries({
          queryKey: [COMMENTS_QUERY_KEY, postId],
        });
        queryClient.invalidateQueries({
          queryKey: [REACTIONS_QUERY_KEY, commentId, userId],
        });
      }
    },
  });
};

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ commentId, postId, postCommentCount }: { commentId: string; postId: string; postCommentCount: number }) => {
      const client = await gqlClient();
      await client.request(DELETE_COMMENT_MUTATION, { itemId: commentId });
      if (countersEnabled) {
        await recountPost(postId);
      } else {
        await client.request(UPDATE_POST_COMMENT_COUNT_MUTATION, {
          itemId: postId,
          commentCount: Math.max(0, (postCommentCount ?? 1) - 1),
        });
      }
    },
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: [COMMENTS_QUERY_KEY, postId] });
      queryClient.invalidateQueries({ queryKey: [POSTS_QUERY_KEY, postId] });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_DELETE_COMMENT") });
      console.error(error);
    },
  });
};

// query saved post
export const useSavedPostsQuery = (options?: { enabled?: boolean }) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  const { data: userProfile } = useGetUserProfileQuery(userId);
  const savedPostIds = userProfile?.savedPostIds ?? [];

  return useQuery({
    queryKey: [POSTS_QUERY_KEY, "saved", userId],
    queryFn: async () => {
      if (!savedPostIds.length) return [];
      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        where: { ItemId: { in: savedPostIds } },
        order: [],
        pageNo: 1,
        pageSize: savedPostIds.length,
      });
      return (result?.getposts?.items as Post[]) ?? [];
    },
    enabled: (options?.enabled ?? true) && !!userId && !!userProfile,
  });
};





// Get users posts
export const useGetUserPostsQuery = (userId: string) => {
  return useInfiniteQuery({
    queryKey: ["USER_POSTS", userId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        where: { CreatedBy: { eq: userId } },
        order: [{ field: "CreatedDate", direction: "DESC" }],
        pageNo: pageParam,
        pageSize: 10,
      });
      return result;
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.getposts?.hasNextPage ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    enabled: !!userId,
  });
};
